import styled from 'styled-components';

import AccordionItem from 'components/AccordionItem';
import Button from 'components/Button';
import HeroSection from 'components/HeroSection';
import RichText from 'components/RichText';
import TestimonialsSlider from 'components/TestimonialsSlider';

import { isEven } from 'utils/helpers';

import BREAKPOINTS from 'styles/breakpoints';
import { CircleMask } from 'src/styles/global';
import COLORS from 'styles/colors';
import { Grid, columns } from 'styles/grid';
import TYPO, { weight } from 'styles/typography';

import Card from './Card';

const Container = styled.div`
  position: relative;
  background-color: ${COLORS.pinkMillennial};
`;

const Hero = styled(HeroSection)`
  color: ${COLORS.pinkMillennial};
  justify-content: flex-end;

  ${BREAKPOINTS.small`
    padding-bottom: 105px;
  `}
`;

const Section = styled.section`
  padding: 60px 0;

  ${BREAKPOINTS.small`
    padding: 100px 0 50px;
  `}
`;

const OpenRolesSection = styled(CircleMask)`
  margin-top: 40px;
  padding: 0 var(--gutter-side) 132px;
  position: static;
  color: ${COLORS.pinkMillennial};
  text-align: center;

  svg path {
    stroke: ${COLORS.pinkMillennial};
  }

  ${BREAKPOINTS.medium`
    margin-top: 50px;
    padding-bottom: 125px;
  `}
`;

const H2 = styled.h2`
  ${TYPO.h3}
  margin: 98px auto 10px;
  max-width: 700px;
  font-weight: ${weight.bold};

  ${BREAKPOINTS.medium`
    margin: 140px auto 40px;
    font-weight: ${weight.black};
    letter-spacing: -0.02em;
  `}

  ${BREAKPOINTS.large`
    margin-top: 200px;
  `}
`;

const RichTextStyled = styled(RichText)`
  margin: 0 auto 25px;
  max-width: 440px;

  *:first-child {
    margin-top: 0;
  }

  ${BREAKPOINTS.medium`
    margin-bottom: 21px;
  `}
`;

const AccordionItems = styled(Grid)`
  padding: 35px 0 0;

  a {
    color: ${COLORS.dawn};
  }

  ${BREAKPOINTS.medium`
    padding-top: 43px;
  `}
`;

const AccordionItemStyled = styled(AccordionItem)`
  ${columns(null, 12)}
  margin: 0 auto;
  max-width: 800px;
  text-align: left;

  ${BREAKPOINTS.medium`
    ${columns(3, 8)}
  `}
`;

const Careers = ({ page }) => {
  const {
    title,
    subtitle,
    heroCta,
    heroBgImg,
    aboutSection,
    testimonialsSlider,
    workingAtClaysSection,
    openRolesTitle,
    openRolesDescription,
    openRolesCta,
    openRoleItems,
  } = page;

  const hasOpenRolesSection =
    openRolesTitle ||
    openRolesDescription ||
    openRolesCta ||
    openRoleItems.length;
  return (
    <Container>
      <Hero title={title} subtitle={subtitle} cta={heroCta} bgImg={heroBgImg} />

      <Section>
        {aboutSection.map(({ id, title, image, textBlock }, idx) => {
          const index = idx + 1;
          return (
            <Card
              key={id}
              idx={index}
              title={title}
              image={image}
              textBlock={textBlock}
              isReverse={isEven(index)}
            />
          );
        })}
      </Section>

      {!!testimonialsSlider.length && (
        <TestimonialsSlider items={testimonialsSlider} />
      )}

      <Section>
        {workingAtClaysSection.map(({ id, title, image, textBlock }, idx) => {
          const index = aboutSection.length + idx + 1;
          return (
            <Card
              key={id}
              idx={index}
              title={title}
              image={image}
              textBlock={textBlock}
              isReverse={isEven(index)}
            />
          );
        })}
      </Section>

      {!!hasOpenRolesSection && (
        <OpenRolesSection bgColor={COLORS.forest}>
          <H2>{openRolesTitle}</H2>

          {openRolesDescription && (
            <RichTextStyled html={openRolesDescription.html} />
          )}

          {openRolesCta && (
            <Button href={openRolesCta.link}>{openRolesCta.label}</Button>
          )}

          {!!openRoleItems.length && (
            <AccordionItems>
              {openRoleItems.map(({ id, label, content }) => (
                <AccordionItemStyled key={id} label={label} content={content} />
              ))}
            </AccordionItems>
          )}
        </OpenRolesSection>
      )}
    </Container>
  );
};

export default Careers;
